import { markupParser, reactRenderer } from 'app/components/common/FormattedText/parser';
import { MarkupRule, QuizBlankLabels } from 'app/components/common/FormattedText/types';
import { notify } from 'app/services/errorReporter';
import { useCss } from 'app/utils/css';
import { Set } from 'immutable';
import React from 'react';

const defaultRuleSet: Set<MarkupRule> = Set([
  MarkupRule.Ruby,
  MarkupRule.Url,
  MarkupRule.Link,
  MarkupRule.Bold,
  MarkupRule.Italic,
  MarkupRule.Strikethrough,
  MarkupRule.InlineCode,
]);

interface IFormattedTextProps {
  text?: string | null;
}

export default function FormattedText({ text }: IFormattedTextProps) {
  return <RenderFormattedText text={text} ruleSet={defaultRuleSet} />;
}

const quizRuleSet: Set<MarkupRule> = Set([...defaultRuleSet, MarkupRule.QuizBlank]);

const QUIZ_BLANK_COMMON = {
  display: 'inline-block',
  borderBottom: '1px solid black',
  minWidth: '80px',
};

interface IFormattedTextWithQuizBlanksProps extends IFormattedTextProps {
  quizBlankLabels: QuizBlankLabels;
}

export function FormattedTextWithQuizBlanks({ text, quizBlankLabels }: IFormattedTextWithQuizBlanksProps) {
  const css = useCss();
  return (
    <RenderFormattedText
      text={text}
      ruleSet={quizRuleSet}
      quizBlankLabels={quizBlankLabels}
      classNames={{
        quizBlank: css('quizBlank', {
          ...QUIZ_BLANK_COMMON,
          position: 'relative',
          top: '0.2em',
        }),
        quizBlankWithLabel: css('quizBlankWithLabel', {
          ...QUIZ_BLANK_COMMON,
          padding: '0 1em',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '1.5',
          position: 'relative',
          top: '-3px',
        }),
      }}
    />
  );
}

const defaultWithNewLinesRuleSet: Set<MarkupRule> = Set([...defaultRuleSet, MarkupRule.Break]);

export function FormattedTextWithNewlines({ text }: IFormattedTextProps) {
  return <RenderFormattedText text={text} ruleSet={defaultWithNewLinesRuleSet} />;
}

export function ImageModalFormattedText({ text }: IFormattedTextProps) {
  const css = useCss();
  return (
    <RenderFormattedText
      text={text}
      ruleSet={defaultRuleSet}
      classNames={{
        link: css('modalLink', {
          color: '#b8b8b8',
          textDecoration: 'none',
        }),
      }}
    />
  );
}
const furiganaRuleSet: Set<MarkupRule> = Set([MarkupRule.Ruby]);

export function FuriganaFormattedText({ text }: IFormattedTextProps) {
  return <RenderFormattedText text={text} ruleSet={furiganaRuleSet} />;
}

const titleRuleSet: Set<MarkupRule> = Set([MarkupRule.Italic, MarkupRule.Ruby]);

export function TitleFormattedText({ text }: IFormattedTextProps) {
  return <RenderFormattedText text={text} ruleSet={titleRuleSet} />;
}

const lineBreakRuleSet: Set<MarkupRule> = Set([MarkupRule.Break]);

export function LineBreakingFormattedText({ text }: IFormattedTextProps) {
  return <RenderFormattedText text={text} ruleSet={lineBreakRuleSet} />;
}

// Custom parsers for words and sentences. Also break on newlines
const uknowRuleSet: Set<MarkupRule> = Set([
  ...defaultRuleSet,
  MarkupRule.Sentence,
  MarkupRule.Word,
  MarkupRule.Break,
]);

export function UKnowAnswerFormattedText({ text }: IFormattedTextProps) {
  return <RenderFormattedText text={text} ruleSet={uknowRuleSet} />;
}

interface IRenderFormattedTextProps extends IFormattedTextProps {
  classNames?: { [index: string]: string };
  quizBlankLabels?: QuizBlankLabels;
  ruleSet: Set<MarkupRule>;
}

/**
 * This is the base component used to render all variants. It allows rendering an arbitrary set of
 * rules. It is not exported, because only the more restricted variants should be used directly.
 */
function RenderFormattedText({ text, ruleSet, classNames = {}, quizBlankLabels }: IRenderFormattedTextProps) {
  // we do it this way instead of default params to also handle nulls
  const inputText = text || '';
  try {
    // Parse the text inline, so that it does not include a wrapping <p> element.
    const syntaxTree = markupParser(inputText, { inline: true, ruleSet });

    // The default output function returns a list of React elements.
    const elements = reactRenderer(syntaxTree, {
      ruleSet,
      classNames,
      quizBlankIndex: 0,
      quizBlankLabels,
    });

    return <span>{elements}</span>;
  } catch (error) {
    if (process.env.NODE_ENV === 'production') {
      // If there is an error during parsing, notify us about the error and return the text as-is.
      notify(error, {
        name: 'RenderFormattedText.render',
        context: { inputText, ruleSet: [...ruleSet] },
      });
      return <span>{inputText}</span>;
    } else {
      throw error;
    }
  }
}
